import { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { tv, VariantProps } from 'tailwind-variants';

/* eslint-disable sonarjs/no-duplicate-string */
const tile = tv({
  base: 'flex items-center justify-center text-center',
  variants: {
    size: {
      '3xs': 'w-4 h-4 max-w-4 max-h-4 min-w-4 min-h-4 text-2xs [&>svg]:w-2.5 [&>svg]:h-2.5 [&>svg]:stroke-2',
      '2xs': 'w-5 h-5 max-w-5 max-h-5 min-w-5 min-h-5 text-xs [&>svg]:w-3 [&>svg]:h-3 [&>svg]:stroke-2',
      xs: 'w-6 h-6 max-w-6 max-h-6 min-w-6 min-h-6 text-xs [&>svg]:w-3 [&>svg]:h-3 [&>svg]:stroke-2',
      sm: 'w-7 h-7 max-w-7 max-h-7 min-w-7 min-h-7 text-xs [&>svg]:w-4 [&>svg]:h-4',
      md: 'w-8 h-8 max-w-8 max-h-8 min-w-8 min-h-8 text-sm [&>svg]:w-4 [&>svg]:h-4',
      lg: 'w-9 h-9 max-w-9 max-h-9 min-w-9 min-h-9 text-sm [&>svg]:w-4 [&>svg]:h-4',
      xl: 'w-10 h-10 max-w-10 max-h-10 min-w-10 min-h-10 text-base [&>svg]:w-5 [&>svg]:h-5',
      '2xl': 'w-11 h-11 max-w-11 max-h-11 min-w-11 min-h-11 text-base [&>svg]:w-5 [&>svg]:h-5',
    },
    radius: {
      full: 'rounded-full',
    },
    isLoading: {
      true: 'animate-pulse',
    },
    color: {
      primary: '',
      secondary: '',
      warning: '',
      success: '',
      danger: '',
    },
    variant: {
      solid: 'border-none',
      flat: 'border-none',
      bordered: 'border',
      light: 'border-none',
    },
  },
  defaultVariants: {
    size: 'md',
    color: 'secondary',
    variant: 'flat',
  },
  compoundVariants: [
    // Loading
    {
      isLoading: true,
      variant: 'solid',
      className: 'bg-secondary-300',
    },
    {
      isLoading: true,
      variant: 'flat',
      className: 'bg-secondary-200',
    },
    {
      isLoading: true,
      variant: 'bordered',
      className: 'border-secondary-300',
    },
    {
      isLoading: true,
      variant: 'light',
      className: 'bg-secondary-200',
    },
    // Solid
    {
      variant: 'solid',
      isLoading: false,
      color: 'primary',
      className: 'bg-primary-500 text-white',
    },
    {
      variant: 'solid',
      isLoading: false,
      color: 'secondary',
      className: 'bg-secondary-950 text-white',
    },
    {
      variant: 'solid',
      isLoading: false,
      color: 'success',
      className: 'bg-success-500 text-white dark:bg-success-700',
    },
    {
      variant: 'solid',
      isLoading: false,
      color: 'warning',
      className: 'bg-warning-500 text-white',
    },
    {
      variant: 'solid',
      isLoading: false,
      color: 'danger',
      className: 'bg-danger-500 text-white',
    },
    // Flat
    {
      variant: 'flat',
      isLoading: false,
      color: 'primary',
      className: 'bg-primary-200 text-primary-500',
    },
    {
      variant: 'flat',
      isLoading: false,
      color: 'secondary',
      className: 'bg-secondary-200 text-secondary-700',
    },
    {
      variant: 'flat',
      isLoading: false,
      color: 'success',
      className: 'bg-success-200 text-success-500',
    },
    {
      variant: 'flat',
      isLoading: false,
      color: 'warning',
      className: 'bg-warning-200 text-warning-500',
    },
    {
      variant: 'flat',
      isLoading: false,
      color: 'danger',
      className: 'bg-danger-200 text-danger-500',
    },
    // Bordered
    {
      variant: 'bordered',
      isLoading: false,
      color: 'primary',
      className: 'text-primary-500 border-primary-300',
    },
    {
      variant: 'bordered',
      isLoading: false,
      color: 'secondary',
      className: 'text-secondary-700 border-secondary-300',
    },
    {
      variant: 'bordered',
      isLoading: false,
      color: 'warning',
      className: 'text-warning-500 border-warning-300',
    },
    {
      variant: 'bordered',
      isLoading: false,
      color: 'success',
      className: 'text-success-500 border-success-300',
    },
    {
      variant: 'bordered',
      isLoading: false,
      color: 'danger',
      className: 'text-danger-500 border-danger-300',
    },
    // Light
    {
      variant: 'light',
      isLoading: false,
      color: 'primary',
      className: 'text-primary-500',
    },
    {
      variant: 'light',
      isLoading: false,
      color: 'secondary',
      className: 'text-secondary-700',
    },
    {
      variant: 'light',
      isLoading: false,
      color: 'warning',
      className: 'text-warning-500',
    },
    {
      variant: 'light',
      isLoading: false,
      color: 'success',
      className: 'text-success-500',
    },
    {
      variant: 'light',
      isLoading: false,
      color: 'danger',
      className: 'text-danger-500',
    },
    // Shape
    {
      size: '3xs',
      radius: undefined,
      className: 'rounded-md',
    },
    {
      size: '2xs',
      radius: undefined,
      className: 'rounded-lg',
    },
    {
      size: 'xs',
      radius: undefined,
      className: 'rounded-lg',
    },
    {
      size: 'sm',
      radius: undefined,
      className: 'rounded-lg',
    },
    {
      size: 'md',
      radius: undefined,
      className: 'rounded-lg',
    },
    {
      size: 'lg',
      radius: undefined,
      className: 'rounded-xl',
    },
    {
      size: 'xl',
      radius: undefined,
      className: 'rounded-xl',
    },
    {
      size: '2xl',
      radius: undefined,
      className: 'rounded-xl',
    },
  ],
});

type TileVariants = VariantProps<typeof tile>;

interface TileProps extends TileVariants {
  children: ReactNode;
  isLoading?: boolean;
  className?: string;
  'data-testid'?: string;
}

export const Tile = ({ children, isLoading, className, ...props }: TileProps) => {
  const computedClassNames = tile({ ...props, isLoading });
  return (
    <div {...props} className={twMerge(computedClassNames, className)}>
      {!isLoading && children}
    </div>
  );
};
